import PubSub, { PUB_SUB_EVENTS } from '@/utils/pub-sub-service';

import ExtensionCommunication from './extensionCommunication';
import {
	MINIMAL_VERSION_NEW_PLACEMENT_RELATIONS,
	EXT_MIN_VERSION_SUPPORT_SIDEBAR,
	MINIMAL_VERSION_ROUTER_GUARD_IFRAME_TOKEN,
} from './extension-communication-service.const';

// private
const CLOSE_PLY_APP_ACTION = 'closePlyApp'; // this should moved to  common-logic between builder && ext.
const WORKFLOW_CREATED = 'WORKFLOW_CREATED';
const askPermissionForAllUrls = async () => {
	const r = await ExtensionCommunication.askAccessForAllUrls();
	const { success } = r;
	return success;
};

// public
const closeWizard = (window) => {
	// postMessage telling the app in extension to hide creation iframe
	window.parent && window.parent.postMessage({ action: CLOSE_PLY_APP_ACTION }, '*');
};
const onWorkflowCreated = (window, workflow) => {
	// postMessage telling the app in extension to hide creation iframe
	if (window.parent && workflow) {
		window.parent.postMessage({ action: WORKFLOW_CREATED, params: { workflowId: workflow.id, projectId: workflow.project } }, '*');
	}
};

const notifyExtensionPageCreated = ExtensionCommunication.notifyExtensionPageCreated;

const checkPermissionForAllUrls = async () => {
	const r = await ExtensionCommunication.hasAccessForAllUrls();
	const { permission } = r;
	return permission;
};

const checkPermission = async (domain) => {
	const r = await ExtensionCommunication.hasAccessForDomain(domain);
	const { permission } = r;
	return permission;
};

const askPermission = async (domain) => {
	const r = await ExtensionCommunication.askAccessForDomain(domain);
	const { success } = r;
	return success;
};

const getVersion = async () => {
	const r = await ExtensionCommunication.getVersion();
	return r?.version;
};
const getCommandBarShortcut = async () => await ExtensionCommunication.getCommandBarShortcut();

const isInstalled = async () => {
	const r = await getVersion();
	return !!r;
};

const reportPinnedExtension = async () => {
	const r = ExtensionCommunication.reportPinnedExtension();
	return r;
};

const askForPermissionIfExtensionInstalled = async (url) => {
	const extensionInstalled = await isInstalled();
	if (extensionInstalled) {
		const hasAccess = await checkPermission(url);
		if (!hasAccess) {
			const granted = await askPermission(url);
			return granted;
		} else {
			return true;
		}
	}
	return false;
};

const askForPermissionToAllUrlsIfExtensionInstalled = async () => {
	const extensionInstalled = await isInstalled();
	if (extensionInstalled) {
		const hasAccess = await checkPermissionForAllUrls();
		if (!hasAccess) {
			const granted = await askPermissionForAllUrls();
			return granted;
		}
		return true;
	}
	return false;
};

const startCreationProcess = (payload) => ExtensionCommunication.startCreationProcess(payload);

const startChangeSampleProcess = (payload) => ExtensionCommunication.startChangeSampleProcess(payload);

import semver from 'semver';
const isExtensionVersionGTEToVersion = async (version) => {
	const extVersion = await getVersion();
	return !!extVersion && semver.gte(extVersion, version);
};

let isExtensionInstalledInterval = null;
let isExtensionSatisfyingVersionInterval = null;

const subscribeToExtensionInstalled = async (callbackFn) => {
	let isExtInstalled = await isInstalled();
	isExtensionInstalledInterval = setInterval(async () => {
		const newIsExtInstalled = await isInstalled();
		if (newIsExtInstalled !== isExtInstalled) {
			isExtInstalled = newIsExtInstalled;
			PubSub.publish(PUB_SUB_EVENTS.IS_EXTENSION_INSTALLED_STATE_CHANGED);
		}
	}, 3000);
	return PubSub.subscribe(PUB_SUB_EVENTS.IS_EXTENSION_INSTALLED_STATE_CHANGED, callbackFn); // returns token of subscription
};

const unsubscribeToExtensionInstalled = (callbackFnOrToken) => {
	PubSub.unsubscribe(PUB_SUB_EVENTS.IS_EXTENSION_INSTALLED_STATE_CHANGED, callbackFnOrToken);
	if (PubSub.getSubscriptions(PUB_SUB_EVENTS.IS_EXTENSION_INSTALLED_STATE_CHANGED).length <= 0) {
		clearInterval(isExtensionInstalledInterval);
	}
};

const subscribeToExtensionVersionSatisfied = async (version, callbackFn) => {
	let isVersionSatisfied = await isExtensionVersionGTEToVersion(version);
	isExtensionSatisfyingVersionInterval = setInterval(async () => {
		const newIsVersionSatisfied = await isExtensionVersionGTEToVersion(version);
		if (newIsVersionSatisfied !== isVersionSatisfied) {
			isVersionSatisfied = newIsVersionSatisfied;
			PubSub.publish(PUB_SUB_EVENTS.IS_EXTENSION_SATISFIED_VERSION_STATE_CHANGED);
		}
	}, 3000);
	return PubSub.subscribe(PUB_SUB_EVENTS.IS_EXTENSION_SATISFIED_VERSION_STATE_CHANGED, callbackFn); // returns token of subscription
};

const unsubscribeToExtensionVersionSatisfied = (callbackFnOrToken) => {
	PubSub.unsubscribe(PUB_SUB_EVENTS.IS_EXTENSION_SATISFIED_VERSION_STATE_CHANGED, callbackFnOrToken);
	if (PubSub.getSubscriptions(PUB_SUB_EVENTS.IS_EXTENSION_SATISFIED_VERSION_STATE_CHANGED).length <= 0) {
		clearInterval(isExtensionSatisfyingVersionInterval);
	}
};

const announceWorkflow = (workflow) => ExtensionCommunication.announceWorkflow(workflow);
const announceWorkflows = (workflowIds) => ExtensionCommunication.announceWorkflows(workflowIds);
const announceButtonDropdown = (buttonDropdown) => ExtensionCommunication.announceButtonDropdown(buttonDropdown);

const doesSupportsNewPlacementRelations = () => isExtensionVersionGTEToVersion(MINIMAL_VERSION_NEW_PLACEMENT_RELATIONS);
const doesSupportsSideBar = () => isExtensionVersionGTEToVersion(EXT_MIN_VERSION_SUPPORT_SIDEBAR);
const doesSupportsRouterGuardTokenLogin = () => isExtensionVersionGTEToVersion(MINIMAL_VERSION_ROUTER_GUARD_IFRAME_TOKEN);

const ExtensionCommunicationService = {
	getCommandBarShortcut,
	closeWizard,
	onWorkflowCreated,
	getVersion,
	isInstalled,
	askPermission,
	checkPermission,
	reportPinnedExtension,
	checkPermissionForAllUrls,
	askForPermissionIfExtensionInstalled,
	askForPermissionToAllUrlsIfExtensionInstalled,
	startCreationProcess,
	isExtensionVersionGTEToVersion,
	startChangeSampleProcess,
	subscribeToExtensionInstalled,
	unsubscribeToExtensionInstalled,
	subscribeToExtensionVersionSatisfied,
	unsubscribeToExtensionVersionSatisfied,
	notifyExtensionPageCreated,
	announceWorkflow,
	announceWorkflows,
	announceButtonDropdown,
	doesSupportsNewPlacementRelations,
	doesSupportsSideBar,
	doesSupportsRouterGuardTokenLogin,
};

export default ExtensionCommunicationService;
